import * as FloatingUI from "@floating-ui/dom"
import * as Log from "web-ui/log"

export function DropdownPlacementPlugin(options) {
  const tomSelect = this

  const dropdownOffset = 8
  const windowPadding = 8
  const flipPadding = options.flipPadding || windowPadding
  const strategy = options.strategy

  let currentPlacement

  let disableAutoUpdate

  tomSelect.hook("instead", "positionDropdown", () => {
    if (!tomSelect.isOpen) {
      return Promise.resolve()
    }

    return updatePosition()
  })

  tomSelect.hook("before", "open", () => {
    if (tomSelect.isOpen || tomSelect.isLocked) {
      return
    }

    if (tomSelect.settings.mode === "multi" && tomSelect.isFull()) {
      return
    }

    tomSelect.lastQuery = null
    tomSelect.refreshOptions(false)
  })

  const sizeMiddleware = FloatingUI.size({
    padding: windowPadding,
    apply: ({ availableHeight, elements }) => {
      const dropdown = elements.floating
      const dropdownContent = tomSelect.dropdown_content

      const surroundingHeight = dropdown.offsetHeight - dropdownContent.offsetHeight
      const availableContentHeight = availableHeight - surroundingHeight

      debug("Resize", { availableHeight, availableContentHeight })

      dropdown.style.setProperty("--combo-box-available-content-height", `${availableContentHeight}px`)
    }
  })

  tomSelect.on("load", () => {
    const config = {
      placement: currentPlacement,
      strategy,
      middleware: [
        FloatingUI.offset(dropdownOffset),
        sizeMiddleware
      ]
    }

    updatePosition(config)
  })

  tomSelect.on("dropdown_open", () => {
    disableAutoUpdate = FloatingUI.autoUpdate(
      tomSelect.control,
      tomSelect.dropdown,
      () => updatePosition(),
      { elementResize: false }
    )
  })

  tomSelect.on("dropdown_close", () => {
    if (disableAutoUpdate) {
      disableAutoUpdate()
      disableAutoUpdate = null
    }

    removeDropdownStyles()
  })

  function updatePosition(config) {
    if (!config) {
      config = {
        placement: "bottom",
        strategy,
        middleware: [
          FloatingUI.offset(dropdownOffset),
          FloatingUI.flip({ padding: flipPadding }),
          sizeMiddleware
        ]
      }
    }

    return FloatingUI.computePosition(
      tomSelect.control,
      tomSelect.dropdown,
      config
    ).then(({ placement, x, y }) => {
      currentPlacement = placement

      if (strategy === "fixed") {
        tomSelect.dropdown.classList.toggle("above", placement === "top")
        tomSelect.dropdown.style.setProperty("position", "fixed")
        tomSelect.dropdown.style.setProperty("z-index", "2")
        placeDropdown({ left: x, top: y })
        return
      }

      const controlRect = tomSelect.control.getBoundingClientRect()

      const left = controlRect.left + window.scrollX

      if (placement === "bottom") {
        const top = y

        placeDropdown({ left, top })

        tomSelect.dropdown.classList.remove("above")
      }

      if (placement === "top") {
        const bodyHeight = document.body.offsetHeight
        const rect = tomSelect.control.getBoundingClientRect()
        const scrollY = window.scrollY
        const scrollbarHeight = getScrollbarHeight()

        const bottom = bodyHeight - rect.top + dropdownOffset - scrollY - scrollbarHeight

        placeDropdown({ left, bottom })

        tomSelect.dropdown.classList.add("above")
      }
    })
  }

  function getScrollbarHeight() {
    const bodyStyle = window.getComputedStyle(document.body)
    const bodyOverflowY = bodyStyle.overflowY
    const hasHiddenOverflow = bodyOverflowY === 'hidden'

    if (!hasHiddenOverflow) {
      return 0
    }

    return document.documentElement.offsetHeight - document.documentElement.clientHeight
  }

  function placeDropdown(coordinates) {
    debug("Coordinates", coordinates)
    const { top, bottom, left } = coordinates

    tomSelect.dropdown.style.setProperty("left", `${left}px`)

    const controlWidth = tomSelect.control.offsetWidth
    tomSelect.dropdown.style.setProperty("min-width", `${controlWidth}px`)

    if (top !== undefined) {
      tomSelect.dropdown.style.setProperty("top", `${top}px`)
      tomSelect.dropdown.style.removeProperty("bottom")
    }

    if (bottom !== undefined) {
      tomSelect.dropdown.style.setProperty("bottom", `${bottom}px`)
      tomSelect.dropdown.style.removeProperty("top")
    }
  }

  function removeDropdownStyles() {
    tomSelect.dropdown.classList.remove("above")
    tomSelect.dropdown.style.removeProperty("--combo-box-available-content-height")
  }

  function debug(message, data) {
    Object.assign(data, {
      id: tomSelect.input.id
    })

    Log.debug("Tom Select Dropdown Placement Plugin - " + message, data)
  }
}
